import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Customer } from '../../types/customerTypes';
import { Invoice } from '../../types/invoiceTypes';
import Button from '../../components/common/Button/Button';
import { formatCurrency, formatDateDMY, formatDateTime, formatDateTimeDMY, drawStyledCompanyName } from '../../utils/helpers';
import invoiceService from '../../services/invoiceService';
import receivedAmountService from '../../services/receivedAmountService';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import styles from './CustomerDetails.module.scss';
import customerService from '../../services/customerService';
import settingsService from '../../services/settingsService';
import { toast } from 'react-toastify';
import reportService, { BalanceReportDetail } from '../../services/reportService';

interface TransactionRow {
  id: string;
  type: 'invoice' | 'received';
  date: string;
  invoice_number?: string;
  amount: number;
  description?: string;
  status?: string;
}

const CustomerDetails: React.FC = () => {
  const navigate = useNavigate();
  // const { id } = useParams<{ id: string }>();
  // const [id, setId] = useState('');
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [receivedAmounts, setReceivedAmounts] = useState<any[]>([]);
  const [transactions, setTransactions] = useState<TransactionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [companyInfo, setCompanyInfo] = useState({ name: 'Invoice Management', address: '' });
  const [balanceDetail, setBalanceDetail] = useState<BalanceReportDetail | null>(null);
  const [balanceLoading, setBalanceLoading] = useState(true);
  const [balanceError, setBalanceError] = useState<string | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const customerInfoRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
     const companyID = localStorage.getItem("customerCompanyId")
     const id = Number(companyID) || 0
    settingsService.getCompanyPublicInfo(id).then(data => {
      setCompanyInfo({
        name: data.name,
        address: data.address,
      });
    });
  }, []);

  useEffect(() => {
    const loadCustomer = async () => {
      setLoading(true);
      try {
        const customerId = localStorage.getItem("customerId")
        const id = customerId || ''
        const customer = await customerService.getCustomer(id);

        setCustomer(customer);
      } catch (error) {
        console.error('Failed to load customer:', error);
        toast.error('Failed to load customer');
        setCustomer(null);
      } finally {
        setLoading(false);
      }
    };

    loadCustomer();
  }, []);

  const handleCustomerPrint = () => {
    const printWindow = window.open('', '', 'height=800,width=1100');
    if (printWindow) {
      printWindow.document.write('<html><head><title>Customer Statement Print</title>');
      printWindow.document.write(`
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          table { border-collapse: collapse; width: 100%; }
          th, td { border: 1px solid #ccc; padding: 8px; }
          th { background: #f5f5f5; }
        </style>
      `);
      printWindow.document.write('</head><body>');
      printWindow.document.write(getCustomerPrintContent());
      printWindow.document.write('</body></html>');
      printWindow.document.close();
      printWindow.print();
    }
  };

  const getCustomerPrintContent = () => {
      if (!customer) return '';
      const now = new Date();
      const printDate = now.toLocaleDateString();
      const printTime = now.toLocaleTimeString();
      const companyName = companyInfo?.name && companyInfo.name.length > 0 ? companyInfo.name : 'Company Name';
      const companyAddress = companyInfo?.address || 'Company Address';
      const firstLetter = companyName.charAt(0);
      const restOfName = companyName.slice(1);
  
      const totalInvoiceAmountLocal = invoices.reduce((sum, inv) => sum + (Number(inv.total_amount) || 0), 0);
      const totalReceivedAmountLocal = receivedAmounts.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
      const totalOutstandingLocal = totalInvoiceAmountLocal - totalReceivedAmountLocal;
  
      return `
        <div style="text-align:center;">
          <h1 style="margin-bottom:0;">
            <span style="color:red; font-size:2rem; font-weight:bold;">${firstLetter}</span>
            <span style="color:green; font-size:1.4rem; font-weight:bold; margin-left:-8px;">${restOfName}</span>
          </h1>
          <div style="font-size:1rem; margin-bottom:6px;">${companyAddress}</div>
          <div style="margin-bottom:6px;">Printed on ${printDate} - ${printTime}</div>
        </div>
         <div style="background:none !important; color:black !important; border:1px solid black; padding:8px; font-size:1.1rem; margin-bottom:8px; -webkit-print-color-adjust: exact; color-adjust: exact; print-color-adjust: exact;">
              Total Outstanding: ${totalOutstandingLocal.toLocaleString()}
            </div>
        <div style="display:flex; justify-content:space-between; font-weight:600; margin-bottom:8px;">
          <div>Customer: ${customer.name}</div>
          <div>Status: ${customer.is_active ? 'Active' : 'Inactive'}</div>
        </div>
        <div style="margin-bottom:8px;">
          <div><strong>Email:</strong> ${customer.email || '-'}</div>
          <div><strong>Phone:</strong> ${customer.phone || '-'}</div>
          <div><strong>Address:</strong> ${customer.address || '-'}</div>
        </div>
        <table style="width:100%; border-collapse:collapse; margin-bottom:8px;">
          <thead>
            <tr>
              <th style="border:1px solid #ccc; padding:6px;">S.No</th>
              <th style="border:1px solid #ccc; padding:6px;">Date/Time</th>
              <th style="border:1px solid #ccc; padding:6px;">Account Head</th>
              <th style="border:1px solid #ccc; padding:6px;">Description</th>
              <th style="border:1px solid #ccc; padding:6px;">Invoice Amount</th>
              <th style="border:1px solid #ccc; padding:6px;">Received Amount</th>
            </tr>
          </thead>
          <tbody>
            ${transactions.map((t, idx) => {
        const invoiceAmt = t.type === 'invoice' ? (Number(t.amount) || 0) : 0;
        const receivedAmt = t.type === 'received' ? (Number(t.amount) || 0) : 0;
        const description =
          t.type === 'invoice'
            ? `${t.description || '-'} - Total`
            : t.description || '-';
  
        // Invoice amount with bold if invoice
        const invoiceCell =
          t.type === 'invoice'
            ? `<strong>${invoiceAmt.toLocaleString()}</strong>`
            : invoiceAmt.toLocaleString();
  
        const baseRow = `
        <tr style="${t.type === 'invoice' ? 'background:#f0f8ff !important; color:#000 !important; -webkit-print-color-adjust: exact; color-adjust: exact; print-color-adjust: exact;' : ''}">
          <td style="border:1px solid #ccc; padding:6px;">${idx + 1}</td>
          <td style="border:1px solid #ccc; padding:6px;">${formatDateDMY(t.date)}</td>
          <td style="border:1px solid #ccc; padding:6px;"></td>
          <td style="border:1px solid #ccc; padding:6px;">${description}</td>
          <td style="border:1px solid #ccc; padding:6px;">${invoiceCell}</td>
          <td style="border:1px solid #ccc; padding:6px;">${receivedAmt.toLocaleString()}</td>
        </tr>
      `;
  
        // Add item details for invoices
        if (t.type === 'invoice' && Array.isArray(invoices) && invoices.length > 0) {
          const invoice = invoices.find(inv => String(inv.invoice_number) === String(t.invoice_number));
          if (invoice && Array.isArray(invoice.items) && invoice.items.length > 0) {
            const itemRows = invoice.items.map((item, itemIdx) => {
              // Check for account head name - it should be account_head_name based on the backend query
              const accountHeadName = item.account_head_name || 'N/A';
  
              return `
                      <tr style="background:#f9fafb;">
                        <td style="border:1px solid #ccc; padding:6px;"></td>
                        <td style="border:1px solid #ccc; padding:6px;"></td>
                        <td style="border:1px solid #ccc; padding:6px;">${accountHeadName}</td>
                        <td style="border:1px solid #ccc; padding:6px; padding-left:20px;">• ${item.description || '-'}</td>
                        <td style="border:1px solid #ccc; padding:6px;">${((Number(item.amount) || 0) + (Number(item.service_charge) || 0)).toLocaleString()}</td>
                        <td style="border:1px solid #ccc; padding:6px;"></td>
                      </tr>
                    `;
            }).join('');
            return baseRow + itemRows;
          }
        }
        return baseRow;
      }).join('')}
            ${transactions.length > 0 ? `
              <tr style="font-weight:bold; background:#f5f5f5;">
                <td colspan="4" style="text-align:right; border:1px solid #ccc; padding:6px;">Total</td>
                <td style="border:1px solid #ccc; padding:6px;">${totalInvoiceAmountLocal.toLocaleString()}</td>
                <td style="border:1px solid #ccc; padding:6px;">${totalReceivedAmountLocal.toLocaleString()}</td>
              </tr>
            ` : ''}
          </tbody>
        </table>
      `;
    };

  useEffect(() => {
    const loadInvoices = async () => {
      setInvoicesLoading(true);
      try {
        const customerId = localStorage.getItem("customerId")
        const id = customerId || ''
        const invoices = await invoiceService.getInvoicesByCustomer(id);
        setInvoices(Array.isArray(invoices) ? invoices : []);
      } catch (error) {
        console.error('Failed to load invoices:', error);
        toast.error('Failed to load customer invoices');
        setInvoices([]);
      } finally {
        setInvoicesLoading(false);
      }
    };

    loadInvoices();
  }, []);

  useEffect(() => {
    const loadReceivedAmounts = async () => {
      try {
        const allReceivedAmounts = await receivedAmountService.getAll();
        const customerId = localStorage.getItem("customerId")
        const id = customerId || ''
        const customerReceivedAmounts = allReceivedAmounts.filter(
          received => String(received.customer_id) === String(id)
        );
        setReceivedAmounts(customerReceivedAmounts);
      } catch (error) {
        console.error('Failed to load received amounts:', error);
        toast.error('Failed to load customer received amounts');
        setReceivedAmounts([]);
      }
    };

    loadReceivedAmounts();
  }, []);

  useEffect(() => {
    const customerId = localStorage.getItem("customerId")
    const id = customerId || ''
    setBalanceLoading(true);
    setBalanceError(null);
    reportService.getBalanceReportDetail(id)
      .then(data => setBalanceDetail(data))
      .catch(() => setBalanceError('Failed to load balance detail'))
      .finally(() => setBalanceLoading(false));
  }, []);

  // Combine invoices and received amounts into transactions
  useEffect(() => {
    const combinedTransactions: TransactionRow[] = [];

    // Add invoices with unique identifiers
    invoices.forEach((invoice, index) => {
      combinedTransactions.push({
        id: `invoice-${invoice.id}-${index}`,
        type: 'invoice',
        date: invoice.created_at,
        invoice_number: invoice.invoice_number,
        amount: Number(invoice.total_amount) || 0,
        description: invoice.invoice_number || 'Invoice',
        status: invoice.status
      });
    });

    // Add received amounts with unique identifiers and better descriptions
    receivedAmounts.forEach((received, index) => {
      combinedTransactions.push({
        id: `received-${received.id}-${index}`,
        type: 'received',
        date: received.created_at,
        amount: Number(received.amount) || 0,
        description: received.notes || received.description
      });
    });

    // Sort by date (newest first), then by ID to maintain consistent ordering
    combinedTransactions.sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      
      if (dateA === dateB) {
        // If dates are same, sort by ID to maintain consistent order
        return a.id.localeCompare(b.id);
      }
      
      return dateB - dateA;
    });

    setTransactions(combinedTransactions);
  }, [invoices, receivedAmounts]);

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}></div>
        <p>Loading customer...</p>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className={styles.errorContainer}>
        <h2>Customer Not Found</h2>
        <p>The customer you're looking for doesn't exist.</p>
        <Button onClick={() => navigate('/customers')}>
          Back to Customers
        </Button>
      </div>
    );
  }

  // FIX: Use consistent calculation with receivedAmounts instead of partially_received_amounts
  const totalInvoiceAmount = invoices.reduce((sum, inv) => sum + (Number(inv.total_amount) || 0), 0);
  const totalReceivedAmount = receivedAmounts.reduce((sum, received) => sum + (Number(received.amount) || 0), 0);
  const totalBalanceAmount = totalInvoiceAmount - totalReceivedAmount;

  // Calculate overall report items total amount (sum of all invoice items' amount + service_charge)
  const itemsTotalAmount = invoices.reduce((sum, inv) => {
    const amount = Array.isArray(inv.items)
      ? inv.items.reduce((itemSum, item) => itemSum + (Number(item.amount) || 0) + (Number(item.service_charge) || 0), 0)
      : 0;
    return sum + amount;
  }, 0);

  return (
    <div className={styles.customerDetail} ref={contentRef}>
      <div className={styles.companyInfo}>
    <h1>{companyInfo.name || 'bala'}</h1>
    {<p>{companyInfo.address || 'aaaaaa'}</p>}
  </div>

  <div className={styles.actions}>
          <Button
            variant="secondary"
            onClick={handleCustomerPrint}
            disabled={isGeneratingPDF}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px' }}>
              <polyline points="6 9 6 2 18 2 18 9" />
              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
              <rect x="6" y="14" width="12" height="8" />
            </svg>
            Print
          </Button>
          </div>
      {/* <div className={styles.header}>
        <div className={styles.titleSection}>
          <h2>{customer.name}</h2>
        </div>
      </div> */}

      <div className={styles.content}>
        <div ref={customerInfoRef}>
          <div className={styles.section}>
            <h3>Contact Information</h3>
            <div className={styles.infoGrid}>
              <div className={styles.infoItem}>
                <label>Name</label>
                <span>{customer.name}</span>
              </div>
              <div className={styles.infoItem}>
                <label>Email Address</label>
                <span>{customer.email}</span>
              </div>
              <div className={styles.infoItem}>
                <label>Phone Number</label>
                <span>{customer.phone}</span>
              </div>
            </div>
          </div>

          <div className={styles.section}>
            <h3>Address Information</h3>
            <div className={styles.infoGrid}>
              <div className={styles.infoItem}>
                <label>Street Address</label>
                <span>{customer.address}</span>
              </div>
              <div className={styles.infoItem}>
                <label>City</label>
                <span>{customer.city}</span>
              </div>
              <div className={styles.infoItem}>
                <label>State/Province</label>
                <span>{customer.state}</span>
              </div>
              <div className={styles.infoItem}>
                <label>ZIP/Postal Code</label>
                <span>{customer.zip_code}</span>
              </div>
              <div className={styles.infoItem}>
                <label>Country</label>
                <span>{customer.country}</span>
              </div>
            </div>
          </div>

          <div className={styles.section}>
            <h3>Summary</h3>
            <div className={styles.infoGrid}>
              <div className={styles.infoItem}>
                <label>Total Invoice Amount</label>
                <span>{formatCurrency(totalInvoiceAmount)}</span>
              </div>
              <div className={styles.infoItem}>
                <label>Total Received Amount</label>
                <span>{formatCurrency(totalReceivedAmount)}</span>
              </div>
              <div className={styles.infoItem}>
                <label>Balance Amount</label>
                <span>{formatCurrency(totalBalanceAmount)}</span>
              </div>
              {/* <div className={styles.infoItem}>
                <label>Report Items Total Amount</label>
                <span>{formatCurrency(itemsTotalAmount)}</span>
              </div> */}
            </div>
          </div>
        </div>

        <div className={styles.section}>
          <h3>Transaction History</h3>
          {invoicesLoading ? (
            <div className={styles.loadingContainer}>
              <div className={styles.loadingSpinner}></div>
              <p>Loading transactions...</p>
            </div>
          ) : transactions.length === 0 ? (
            <div className={styles.emptyState}>
              <p>No transactions found for this customer.</p>
            </div>
          ) : (
            <div className={styles.invoiceTable}>
              <table>
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Date</th>
                    <th>Invoice #</th>
                    <th>Description</th>
                    <th>Amount</th>
                    {/* <th>Status</th> */}
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((transaction) => (
                    <React.Fragment key={transaction.id}>
                      <tr className={transaction.type === 'received' ? styles.receivedRow : styles.invoiceRow}>
                        <td>
                          <span className={`${styles.typeBadge} ${styles[transaction.type]}`}>
                            {transaction.type === 'invoice' ? 'Invoice' : 'Payment'}
                          </span>
                        </td>
                        <td>{formatDateTimeDMY(transaction.date)}</td>
                        <td>{transaction.invoice_number || '-'}</td>
                        <td>{transaction.description || '-'}</td>
                        <td className={transaction.type === 'received' ? styles.receivedAmount : styles.invoiceAmount}>
                          {transaction.type === 'received' ? '+' : ''}{formatCurrency(transaction.amount)}
                        </td>
                      </tr>
                      {/* Show invoice items if this is an invoice and items are available */}
                      {transaction.type === 'invoice' && Array.isArray(invoices) && invoices.length > 0 && (() => {
                        const invoice = invoices.find(inv => String(inv.invoice_number) === String(transaction.invoice_number));
                        if (invoice && Array.isArray(invoice.items) && invoice.items.length > 0) {
                          return (
                            <tr>
                              <td colSpan={6} style={{ padding: 0, background: '#f9fafb' }}>
                                <table style={{ width: '100%', margin: 0, background: '#f9fafb' }}>
                                  <thead>
                                    <tr>
                                      <th style={{ fontWeight: 500 }}>Item Description</th>
                                      <th style={{ fontWeight: 500 }}>Item Total Amount</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {invoice.items.map((item, idx) => (
                                      <tr key={idx}>
                                        <td>{item.description || '-'}</td>
                                        <td>{formatCurrency((Number(item.amount) || 0) + (Number(item.service_charge) || 0))}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </td>
                            </tr>
                          );
                        }
                        return null;
                      })()}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerDetails;
